import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { format } from 'date-fns';

/**
 * Export data to CSV
 * @param {Array} data - Array of objects containing the row data
 * @param {string} filename - Base name for the exported file
 * @param {Array} columns - Array of column definitions: { header: 'Title', dataKey: 'key' }
 */
export const exportToCSV = (data, filename, columns) => {
  if (!data || !data.length) return;

  // Build header row
  const header = columns.map(col => `"${col.header.replace(/"/g, '""')}"`).join(',');
  
  // Build data rows
  const rows = data.map(row => {
    return columns.map(col => {
      let cellData = row[col.dataKey];
      if (cellData === null || cellData === undefined) cellData = '';
      // Escape quotes and wrap in quotes
      return `"${String(cellData).replace(/"/g, '""')}"`;
    }).join(',');
  });

  const csvContent = [header, ...rows].join('\n');
  
  // Create Blob and trigger download (UTF-8 BOM for Excel support)
  const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  
  link.setAttribute('href', url);
  link.setAttribute('download', `${filename}_${format(new Date(), 'yyyy-MM-dd')}.csv`);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

/**
 * Export data to Excel (XLSX)
 * @param {Array} data - Array of objects containing the row data
 * @param {string} filename - Base name for the exported file
 * @param {Array} columns - Array of column definitions: { header: 'Title', dataKey: 'key' }
 */
export const exportToExcel = (data, filename, columns) => {
  if (!data || !data.length) return;

  // Transform data to match headers
  const formattedData = data.map(row => {
    const newRow = {};
    columns.forEach(col => {
      newRow[col.header] = row[col.dataKey];
    });
    return newRow;
  });

  const worksheet = XLSX.utils.json_to_sheet(formattedData);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Export');

  // Auto-size columns slightly
  const wscols = columns.map(() => ({ wch: 20 }));
  worksheet['!cols'] = wscols;

  XLSX.writeFile(workbook, `${filename}_${format(new Date(), 'yyyy-MM-dd')}.xlsx`);
};

/**
 * Export data to PDF
 * @param {Array} data - Array of objects containing the row data
 * @param {string} filename - Base name for the exported file
 * @param {Array} columns - Array of column definitions: { header: 'Title', dataKey: 'key' }
 * @param {string} title - Document title to display at the top
 */
export const exportToPDF = (data, filename, columns, title = 'Rapport') => {
  if (!data || !data.length) return;

  // Initialize PDF (landscape for better table fitting)
  const doc = new jsPDF('landscape');
  
  // Add Title
  doc.setFontSize(18);
  doc.text(title, 14, 22);
  
  // Add Date
  doc.setFontSize(11);
  doc.setTextColor(100);
  doc.text(`Généré le: ${format(new Date(), 'dd/MM/yyyy HH:mm')}`, 14, 30);

  // Prepare table data
  const tableData = data.map(row => columns.map(col => row[col.dataKey] || ''));
  const head = [columns.map(col => col.header)];

  // Generate Table
  autoTable(doc, {
    head: head,
    body: tableData,
    startY: 40,
    theme: 'striped',
    styles: {
      fontSize: 10,
      cellPadding: 3,
    },
    headStyles: {
      fillColor: [220, 38, 38], // Red-ish theme to match Mizan
      textColor: 255,
      fontStyle: 'bold'
    },
    alternateRowStyles: {
      fillColor: [249, 250, 251]
    }
  });

  doc.save(`${filename}_${format(new Date(), 'yyyy-MM-dd')}.pdf`);
};
